import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Column} from "@ant-design/charts";
import {useTranslation} from "react-i18next";
import {Button, Col, DatePicker, Row, Select, Table, Tag} from "antd";
import {FilterOutlined, ClearOutlined} from "@ant-design/icons";
import {ColumnsType, TablePaginationConfig} from "antd/lib/table";
import {Link} from "react-router-dom";
import moment from "moment";
import {RangePickerProps} from "antd/lib/date-picker";
import {Card, Label} from "../../../components/ui";
import api from "../../../services/api";
import {useToast} from "../../../hooks/toast";

import "./styles.less";
import {IContractor} from "../../Contractors";
import filterOptions from "../../../utils/filterOptions";
import {FiltersCard} from "../../../components/Filters";
import ProjectTypes from "../../../enums/ProjectTypes";
import FormItem from "../../../components/FormItem";
import {IProjectType} from "../../Projects";

const {RangePicker} = DatePicker;

interface IStat {
  month: string;
  estimated: number;
  wasted: number;
}

interface IProject {
  id: string;
  short_description: string;
  identifier: string;
  contractor_name: string;
  estimated: number;
  wasted: number;
}

interface IConcludedProjectStats {
  stats: IStat[];
  projects: IProject[];
}

interface Iidentifier {
  value: IProject[];
  cache: "NO-CACHE" | "CACHE";
}

const ConcludedProjectsStats: React.FC = () => {
  const {t} = useTranslation(["projects", "dashboards"]);

  const [projectsTypes, setProjectsTypes] = useState<IProjectType[]>([]);
  const [loading, setLoading] = useState({
    status: false,
    isLoading: true,
    global: false,
  });
  const [data, setData] = useState<IConcludedProjectStats>();
  const [identifiers, setIdentifiers] = useState<Iidentifier>({
    value: [],
    cache: "CACHE",
  });
  const [startMonth, setStartMonth] = useState(`01-${moment().format("YYYY")}`);
  const [endMonth, setEndMonth] = useState(
    `01-${moment().add(1, "year").format("YYYY")}`,
  );
  const [contractors, setContractors] = useState<IContractor[]>([]);

  const [showFilters, setShowFilters] = useState(false);
  const [filterContraName, setFilterContraName] = useState<string | undefined>(
    undefined,
  );
  const [filterIdentifier, setFilterIdentifier] = useState<string | undefined>(
    undefined,
  );
  const [filterTypeProjects, setFilterTypeProjects] = useState<
    string | undefined
  >(undefined);

  const {addToast} = useToast();

  const iconFilter = useMemo(() => {
    return showFilters ? <ClearOutlined/> : <FilterOutlined/>;
  }, [showFilters]);

  useEffect(() => {
    setLoading(exec => {
      if (exec.isLoading) {
        return {
          ...exec,
          status: true,
          isLoading: false,
        };
      }
      return {...exec, global: true};
    });
    api
      .get<IConcludedProjectStats>("/dashboards/concluded-projects", {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          identifier: filterIdentifier,
          contractor_name: filterContraName,
          project_type_id: filterTypeProjects,
        },
      })
      .then(response => {
        setData(response.data);
        setIdentifiers(old => {
          if (old.cache === "CACHE") {
            return {
              value: response.data.projects,
              cache: "NO-CACHE",
            };
          }
          return old;
        });
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os projetos, tente novamente.",
        });
      })
      .finally(() =>
        setLoading(old => {
          return {...old, status: false, global: false};
        }),
      );
  }, [addToast, endMonth, startMonth, filterContraName, filterIdentifier, filterTypeProjects]);

  useEffect(() => {
    api
      .get<IContractor[]>("/contractors")
      .then(response => setContractors(response.data))
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os contratantes, tente novamente.",
        });
      });
  }, [addToast]);

  useEffect(() => {
    api
      .get<IProjectType[]>("/project-types")
      .then(response => setProjectsTypes(response.data))
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar tipos de projetos",
          description: "Ocorreu um erro ao buscar os tipos de projetos",
        });
      });
  }, [addToast]);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      defaultCurrent: 1,
      total: data?.projects.length,
      defaultPageSize: 10,
    }),
    [data],
  );

  const stats = useMemo(
    () =>
      data?.stats.flatMap(({estimated, month, wasted}) => [
        {
          month: moment(month, "MM-YYYY").format("MMMM YYYY"),
          name: t("estimated-hours"),
          value: estimated,
        },
        {
          month: moment(month, "MM-YYYY").format("MMMM YYYY"),
          name: t("wasted-hours"),
          value: wasted,
        },
      ]) || [],
    [data?.stats, t],
  );

  const projectOption = useMemo(() => {
    return projectsTypes.map(projectType => ({
      value: projectType.id,
      label: t(`project-types.options.${projectType.acronym}.label`),
    }));
  }, [t, projectsTypes]);

  const config = {
    data: stats,
    title: "Test",
    isGroup: true,
    xField: "month",
    yField: "value",
    seriesField: "name",
    height: 250,
    /** 设置颜色 */
    color: ["#3389eb", "#f39016ff"],

    /** 设置间距 */
    // marginRatio: 0.1,
    // label: {
    //   // 可手动配置 label 数据标签位置
    //   position: "middle",
    //   // 'top', 'middle', 'bottom'
    //   // 可配置附加的布局方法
    //   layout: [
    //     // 柱形图数据标签位置自动调整
    //     {
    //       type: "interval-adjust-position",
    //     }, // 数据标签防遮挡
    //     {
    //       type: "interval-hide-overlap",
    //     }, // 数据标签文颜色自动调整
    //     {
    //       type: "adjust-color",
    //     },
    //   ],
    // },
  };

  //   --dark-orange: #e95011ff;
  // --light-orange: #f39016ff;
  // --dark-grey: #242424ff;
  const projectColumns: ColumnsType<IProject> = [
    {
      title: t("identifier"),
      dataIndex: "identifier",
    },
    {
      title: t("short_description"),
      dataIndex: "short_description",
      render: (description, project: IProject) => {
        return (
          <>
            <Link to={`/app/projects/${project.id}`}>{description}</Link>
          </>
        );
      },
    },
    {
      title: t("type"),
      dataIndex: "project_type",
      render: (project_type: string) => {
        return (<Tag
          color={t(`project-types.options.${project_type}.color`)}
          key={project_type}
        >
          {t(`project-types.options.${project_type}.label`)}
        </Tag>)
      }
    },
    {
      title: t("contractor_name"),
      dataIndex: "contractor_name",
    },
    {
      title: t("estimated"),
      dataIndex: "estimated",
    },
    {
      title: t("wasted"),
      dataIndex: "wasted",
    },
  ];

  const handleFilters = () => {
    if (showFilters) {
      setShowFilters(old => !old);
      setFilterIdentifier(undefined);
      setFilterContraName(undefined);
      setFilterTypeProjects(undefined);
      return;
    }

    setShowFilters(old => !old);
  };

  const projectIdentifierOptions = useMemo(() => {
    return identifiers.value
      .filter(project => project?.identifier)
      .map(project => {
        return {
          label: project.identifier,
          value: project.identifier,
        };
      });
  }, [identifiers]);

  const contractorsOptions = useMemo(() => {
    return contractors
      .map(contractor => {
        return {
          label: contractor.name,
          value: contractor.name,
        };
      })
      .filter(contractor => contractor.label !== undefined);
  }, [contractors]);

  const onChangeDates: RangePickerProps["onChange"] = useCallback(
    (dates, _) => {
      if (dates) {
        setStartMonth(dates[0].format("MM-YYYY"));
        setEndMonth(dates[1].format("MM-YYYY"));
        setIdentifiers(old => {
          return {
            value: old.value,
            cache: "CACHE",
          };
        });
      }
    },
    [],
  );

  const disabledDate: RangePickerProps["disabledDate"] = current => {
    // Can not select days before today and today
    return current && current > moment().endOf("month");
  };

  return (
    <Card
      loading={loading.status}
      extra={
        <>
          <RangePicker
            onChange={onChangeDates}
            format="MMMM YYYY"
            disabledDate={disabledDate}
            picker="month"
            allowClear={false}
            value={[moment(startMonth, "MM-YYYY"), moment(endMonth, "MM-YYYY")]}
          />
          <Button
            icon={iconFilter}
            type={showFilters ? "primary" : "link"}
            className="filters-button"
            onClick={() => handleFilters()}
          />
        </>
      }
      title={t("concluded-projects", {ns: "dashboards"})}
    >
      {/* <Col lg={6} md={24} xl={6}> */}

      {showFilters ? (
        <FiltersCard>

          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("contractor_name")}</Label>
            </div>
            <Select
              loading={loading.global}
              disabled={loading.global}
              placeholder={t("contractor_name")}
              style={{width: "100%"}}
              allowClear
              showSearch
              filterOption={filterOptions}
              options={contractorsOptions}
              onChange={value => {
                setIdentifiers(old => {
                  return {
                    ...old,
                    cache: "CACHE",
                  };
                });
                setFilterContraName(value);
                setFilterIdentifier(undefined);
              }}
            />
          </Col>

          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("Identificadores")}</Label>
            </div>
            <Select
              key={filterContraName}
              disabled={loading.global}
              loading={loading.global}
              placeholder={t("Identificadores")}
              style={{width: "100%"}}
              allowClear
              showSearch
              filterOption={filterOptions}
              options={projectIdentifierOptions}
              onChange={value => setFilterIdentifier(value)}
            />
          </Col>


          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("project-types.label", {ns: "projects"})}</Label>
            </div>
            <Select
              showSearch
              allowClear
              disabled={loading.global}
              style={{width: "100%"}}
              placeholder={t("project-types.placeholder")}
              onChange={
                projectType => setFilterTypeProjects(projectType)
                // ts-ignore
              }
              options={projectOption}
              filterOption={filterOptions}
            />
          </Col>

        </FiltersCard>
      ) : null}
      <Column {...config} />
      {/* </Col> */}
      {/* <Col lg={18} md={24} xl={18}> */}
      <Table
        className="table-striped"
        style={{marginTop: 20}}
        scroll={{
          x: "max-content",
          // y: maxHeight,
        }}
        dataSource={data?.projects || []}
        rowKey={record => record.id}
        columns={projectColumns}
        pagination={pagination}
        size="small"
      />
      {/* </Col> */}
    </Card>
  );
};

export default ConcludedProjectsStats;
